import { type Registry as Services, service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import ProtectedRoute from 'uplisting-frontend/pods/base/protected-route';
import type ProtectionDisputesController from 'uplisting-frontend/pods/protection/disputes/controller';

interface IParams extends Record<string, unknown> {
  propertyIds: string[];
  statuses: string[];
  page: number;
}

export default class ProtectionDisputesRoute extends ProtectedRoute {
  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  async beforeModel(transition: Transition): Promise<void> {
    await super.beforeModel(transition);

    if (!this.currentUser.hasAccessTo('disputes.index')) {
      transition.abort();

      this.router.transitionTo('protection.overview');
    }
  }

  // The list response deliberately carries no provider detail, so it has to land
  // before the dispute route's own findRecord — not after it in setupController.
  async model(params: IParams): Promise<void> {
    const controller = this.controllerFor(
      'protection.disputes',
    ) as ProtectionDisputesController;

    Object.assign(controller, params);

    await Promise.all([
      this.propertyRepository.findAll(),
      controller.fetchData(),
    ]);
  }

  // The fetch in `model` clamps an out-of-range page on the controller, but a
  // query param changed mid-transition never reaches the URL — it has to be
  // replaced once the transition has settled.
  afterModel(_resolvedModel: unknown, transition: Transition): void {
    const controller = this.controllerFor(
      'protection.disputes',
    ) as ProtectionDisputesController;

    const requested = Number(transition.to?.queryParams?.['page'] ?? 1);

    if (controller.totalPages > 0 && requested > controller.totalPages) {
      const page = controller.totalPages;

      void transition
        .then(() => {
          if (this.router.currentRouteName?.startsWith('protection.disputes')) {
            this.router.replaceWith('protection.disputes', {
              queryParams: { page },
            });
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {});
    }
  }

  resetController(
    controller: ProtectionDisputesController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting) {
      controller.resetFilters();
      controller.disputes = [];
      controller.searchQuery = '';
      controller.page = 1;
      controller.totalPages = 0;
    }
  }
}
